import { useEffect, useRef, useState } from "react";
import { emailSchema, type UpdateProfileResponse, type User } from "@gull/shared";
import { enduserApi, ApiError } from "../../../lib/api";
import { BANK_ACCOUNT_RE, PHONE_RE, POSTAL_CODE_RE } from "../constants";
import { emptyShippingAddress, type ShippingAddressDraft } from "../types";

const isValidEmail = (value: string) => emailSchema.safeParse(value.trim()).success;

export function useContactDetails(profile: User | null | undefined, fallbackEmail: string | null) {
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [address, setAddress] = useState<ShippingAddressDraft>(emptyShippingAddress);
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankAccountTouched, setBankAccountTouched] = useState(false);
  const [kontoEier, setKontoEier] = useState("");
  const [persistError, setPersistError] = useState<string | null>(null);
  const prefilledFromProfile = useRef(false);

  useEffect(() => {
    if (!profile || prefilledFromProfile.current) return;
    prefilledFromProfile.current = true;
    setEmail((prev) => prev || profile.email || fallbackEmail || "");
    setAddress((prev) => ({
      line1: prev.line1 || profile.address || "",
      line2: prev.line2,
      postalCode: prev.postalCode || profile.postalCode || "",
      city: prev.city || profile.city || "",
      country: prev.country || "NO",
    }));
    setBankAccountNumber((prev) => prev || profile.bankAccountNumber || "");
    setKontoEier((prev) => prev || profile.kontoEier || profile.name || "");
  }, [profile, fallbackEmail]);

  const updateAddress = (patch: Partial<ShippingAddressDraft>) =>
    setAddress((prev) => ({ ...prev, ...patch }));

  const updateEmail = (value: string) => {
    setEmail(value);
    if (emailTouched) setEmailTouched(false);
  };

  const updateBankAccountNumber = (value: string) => {
    setBankAccountNumber(value);
    if (bankAccountTouched) setBankAccountTouched(false);
  };

  const applySavedBankAccount = () => {
    setBankAccountNumber(profile?.bankAccountNumber ?? "");
    setKontoEier(profile?.kontoEier ?? profile?.name ?? "");
    setBankAccountTouched(false);
  };

  const emailError =
    emailTouched && !isValidEmail(email)
      ? "Ugyldig e-postadresse. Bruk formatet navn@domene.no."
      : null;

  const bankAccountError =
    bankAccountTouched &&
    bankAccountNumber.trim() &&
    !BANK_ACCOUNT_RE.test(bankAccountNumber.trim())
      ? "Ugyldig kontonummer. Bruk 11 siffer eller XXXX.XX.XXXXX."
      : null;

  const isComplete =
    isValidEmail(email) &&
    !!address.line1.trim() &&
    POSTAL_CODE_RE.test(address.postalCode) &&
    !!address.city.trim() &&
    (!bankAccountNumber || BANK_ACCOUNT_RE.test(bankAccountNumber.trim())) &&
    (!bankAccountNumber || !!kontoEier.trim()) &&
    (!profile?.phone || PHONE_RE.test(profile.phone.replace(/\s/g, "")));

  const markTouched = () => {
    setEmailTouched(true);
    setBankAccountTouched(true);
  };

  const persist = async () => {
    const patch: Record<string, string> = {};
    const nextEmail = email.trim().toLowerCase();
    const nextAddress = address.line1.trim();
    const nextPostalCode = address.postalCode.trim();
    const nextCity = address.city.trim();
    const nextBankAccount = bankAccountNumber.trim();
    const nextKontoEier = kontoEier.trim();

    if (nextEmail && nextEmail !== (profile?.email ?? "")) patch.email = nextEmail;
    if (nextAddress && nextAddress !== (profile?.address ?? "")) patch.address = nextAddress;
    if (nextPostalCode && nextPostalCode !== (profile?.postalCode ?? "")) {
      patch.postalCode = nextPostalCode;
    }
    if (nextCity && nextCity !== (profile?.city ?? "")) patch.city = nextCity;
    if (nextBankAccount && nextBankAccount !== (profile?.bankAccountNumber ?? "")) {
      patch.bankAccountNumber = nextBankAccount;
    }
    if (nextKontoEier && nextKontoEier !== (profile?.kontoEier ?? "")) {
      patch.kontoEier = nextKontoEier;
    }

    if (Object.keys(patch).length === 0) {
      setPersistError(null);
      return true;
    }

    try {
      await enduserApi.authedPatch<UpdateProfileResponse>("/me", patch);
      setPersistError(null);
      return true;
    } catch (err) {
      setPersistError(
        err instanceof ApiError ? err.message : "Kunne ikke lagre kontaktinformasjonen",
      );
      return false;
    }
  };

  return {
    email,
    emailValid: isValidEmail(email),
    emailError,
    updateEmail,
    markEmailTouched: () => setEmailTouched(true),
    address,
    updateAddress,
    bankAccountNumber,
    bankAccountError,
    updateBankAccountNumber,
    markBankAccountTouched: () => setBankAccountTouched(true),
    kontoEier,
    setKontoEier,
    savedBankAccountAvailable:
      !!profile?.bankAccountNumber && profile.bankAccountNumber !== bankAccountNumber,
    applySavedBankAccount,
    isComplete,
    markTouched,
    persist,
    persistError,
  };
}

export type ContactDetails = ReturnType<typeof useContactDetails>;
